import { UNRESOLVED_OVERLAP_STATUSES, type AgentRun } from "@botallion/contracts";
import { showConnectCommand } from "@botallion/ui";
import { RUN_TONE, runnerLabel, runStatusLabel } from "../describe.ts";
import { html, type Html } from "../html.ts";
import { ago, chip, empty, type Tone, type ViewContext } from "./common.ts";

const STATUS_TONE: Record<string, Tone> = { active: "good", idle: "warn", landed: "info", expired: "muted" };

const tokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 10_000 ? `${Math.round(n / 1000)}k` : String(n));

/** One run (WP-17): status, where it runs, budget used, and why it is in its status. */
function runLine(r: AgentRun): Html {
  const used = Math.round((r.usage.tokens / r.budget.tokens) * 100);
  return html`<div class="run ${r.status}">
    <div class="row meta">
      ${chip(runStatusLabel(r.status), RUN_TONE[r.status])}
      <span>${runnerLabel(r.runner)}${r.attempt > 1 ? `, attempt ${r.attempt}` : ""}</span>
      <span class="budget" title="${r.usage.requests} model calls; ${r.budget.minutes}-minute budget">${tokens(r.usage.tokens)} of ${tokens(r.budget.tokens)} tokens (${used}%)</span>
    </div>
    <div class="reason">${r.reason}</div>
  </div>`;
}

/** View 1: each session, its work package, status note, heartbeat and runs, then runs whose agent has not joined. */
export function agentsView(ctx: ViewContext): Html {
  const { state, now } = ctx;
  const sessions = Object.values(state.sessions);
  const runs = Object.values(state.runs).sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const unjoined = runs.filter((r) => r.sessionId === null || !state.sessions[r.sessionId]);
  if (!sessions.length && !unjoined.length) return empty(showConnectCommand ? "No agents yet. Agents appear here when they join. Connect Claude Code: run npx botallion connect." : "No agents yet. Agents appear here when they join.");
  return html`<ul class="agents">${sessions.map((s) => {
    const pkg = state.packages[s.wpId];
    const claims = Object.values(state.claims).filter((c) => c.sessionId === s.id && (c.status === "active" || c.status === "queued"));
    const open = Object.values(state.overlaps).filter(
      (o) => (o.sessionA === s.id || o.sessionB === s.id) && UNRESOLVED_OVERLAP_STATUSES.includes(o.status),
    );
    const blocking = open.filter((o) => o.severity === "block").length;
    const fresh = now - Date.parse(s.lastHeartbeat) < 75_000 && s.status === "active";
    return html`<li class="agent ${s.status}">
      <div class="row"><strong class="name">${s.agentName}</strong>${s.vendor ? html`<span class="vendor" title="Agent product">${s.vendor}</span>` : ""}${chip(s.status, STATUS_TONE[s.status] ?? "muted")}</div>
      <div class="wp">${s.wpId}${pkg ? html` <span class="title">${pkg.title}</span>` : ""}</div>
      <div class="note">${s.note ?? html`<span class="muted">no status note yet</span>`}</div>
      <div class="row meta">
        <span class="beat ${fresh ? "fresh" : ""}" title="${s.lastHeartbeat}">heartbeat ${ago(now, s.lastHeartbeat)}</span>
        <span>${claims.length} claim${claims.length === 1 ? "" : "s"}</span>
        ${open.length ? chip(`${open.length} open overlap${open.length === 1 ? "" : "s"}${blocking ? `, ${blocking} blocking` : ""}`, blocking ? "block" : "warn") : ""}
      </div>
      ${runs.filter((r) => r.sessionId === s.id).map(runLine)}
    </li>`;
  })}</ul>
  ${unjoined.length
    ? html`<h3 class="unjoined">Runs without a session yet</h3>
      <ul class="agents">${unjoined.map((r) => {
        const pkg = state.packages[r.wpId];
        return html`<li class="agent run-only ${r.status}">
          <div class="row"><strong class="name">${r.agentName ?? html`<span class="muted">agent not launched</span>`}</strong></div>
          <div class="wp">${r.wpId}${pkg ? html` <span class="title">${pkg.title}</span>` : ""}</div>
          ${runLine(r)}
        </li>`;
      })}</ul>`
    : ""}`;
}
